<template>
  <div class="course-vjs-wrap" :class="{ 'course-vjs-wrap--landscape': landscapeFs, 'course-vjs-wrap--fill': fill }">
    <div class="course-vjs-wrap__stage">
      <audio v-if="isAudio" ref="videoPlayer" class="video-js vjs-default-skin vjs-big-play-centered"></audio>
      <video
        v-else
        ref="videoPlayer"
        class="video-js vjs-default-skin vjs-big-play-centered"
        playsinline
        webkit-playsinline
        x5-playsinline="true"
        x5-video-player-type="h5"
        x5-video-player-fullscreen="true"
        x5-video-orientation="landscape"
      ></video>
    </div>
    <div v-if="!isAudio" class="course-vjs-wrap__actions">
      <button type="button" class="course-vjs-wrap__btn" @click="toggleLandscape">
        {{ landscapeFs ? '退出全屏' : '横屏全屏' }}
      </button>
    </div>
  </div>
</template>

<script>
import videojs from 'video.js'
import 'video.js/dist/video-js.css'
import { installHlsKeyAuth } from '@/utils/hlsKeyAuth'
import {
  applyWechatVideoAttrs,
  exitNativeFullscreen,
  tryLockLandscape,
  tryNativeVideoFullscreen,
  tryUnlockOrientation
} from '@/utils/wechatVideo'

export default {
  name: 'VideoPlayer',
  props: {
    isAudio: {
      type: Boolean,
      default: false
    },
    options: {
      type: Object,
      default() {
        return {}
      }
    },
    fill: {
      type: Boolean,
      default: false
    },
    autoplay: {
      type: Boolean,
      default: false
    }
  },
  data() {
    return {
      player: null,
      landscapeFs: false
    }
  },
  mounted() {
    installHlsKeyAuth()
    const opts = {
      controls: true,
      preload: 'auto',
      playsinline: true,
      ...this.options,
      fluid: this.fill ? false : this.options.fluid !== false,
      fill: this.fill,
      autoplay: this.autoplay || !!this.options.autoplay
    }
    this.player = videojs(this.$refs.videoPlayer, opts, () => {
      const tech = this.player.tech(true)
      const el = tech && tech.el ? tech.el() : this.$refs.videoPlayer
      applyWechatVideoAttrs(el)
      if (opts.autoplay) {
        const pending = this.player.play()
        if (pending && typeof pending.catch === 'function') pending.catch(() => {})
      }
    })
    document.addEventListener('keydown', this.onKeydown)
  },
  beforeUnmount() {
    document.removeEventListener('keydown', this.onKeydown)
    this.leaveLandscape(true)
    if (this.player) {
      this.player.dispose()
      this.player = null
    }
  },
  methods: {
    onKeydown(e) {
      if (e.key === 'Escape' && this.landscapeFs) this.leaveLandscape()
    },
    async toggleLandscape() {
      if (this.landscapeFs) {
        await this.leaveLandscape()
        return
      }
      const el = this.$refs.videoPlayer
      applyWechatVideoAttrs(el)
      await tryNativeVideoFullscreen(el)
      await tryLockLandscape()
      this.landscapeFs = true
      document.body.classList.add('course-video-landscape-lock')
      if (this.player) {
        try {
          this.player.play()
        } catch (_) {
          /* ignore */
        }
      }
    },
    async leaveLandscape(silent) {
      this.landscapeFs = false
      document.body.classList.remove('course-video-landscape-lock')
      await exitNativeFullscreen()
      await tryUnlockOrientation()
      if (!silent && this.player) {
        try {
          this.player.trigger('resize')
        } catch (_) {
          /* ignore */
        }
      }
    }
  }
}
</script>

<style scoped>
.course-vjs-wrap {
  width: 100%;
}

.course-vjs-wrap--fill {
  flex: 1;
  min-height: 0;
  height: 100%;
  display: flex;
  flex-direction: column;
}

.course-vjs-wrap--fill .course-vjs-wrap__stage {
  flex: 1;
  min-height: 0;
  display: flex;
  flex-direction: column;
}

.course-vjs-wrap--fill .course-vjs-wrap__stage :deep(.video-js) {
  flex: 1;
  width: 100% !important;
  height: 100% !important;
  min-height: 0;
  padding-top: 0 !important;
}

.course-vjs-wrap__stage {
  width: 100%;
  background: #000;
  border-radius: 8px;
  overflow: hidden;
}

.course-vjs-wrap__stage :deep(.video-js) {
  width: 100%;
  min-height: 200px;
}

.course-vjs-wrap__actions {
  display: flex;
  gap: 8px;
  margin-top: 8px;
}

.course-vjs-wrap__btn {
  border: 1px solid #dcdfe6;
  background: #fff;
  color: #303133;
  border-radius: 8px;
  padding: 8px 12px;
  font-size: 13px;
  cursor: pointer;
}

.course-vjs-wrap--landscape {
  position: fixed;
  inset: 0;
  z-index: 10050;
  background: #000;
  display: flex;
  align-items: center;
  justify-content: center;
}

.course-vjs-wrap--landscape .course-vjs-wrap__stage {
  position: absolute;
  top: 50%;
  left: 50%;
  width: 100vh;
  height: 100vw;
  border-radius: 0;
  transform: translate(-50%, -50%) rotate(90deg);
}

.course-vjs-wrap--landscape .course-vjs-wrap__stage :deep(.video-js) {
  width: 100% !important;
  height: 100% !important;
  min-height: 0;
  padding-top: 0 !important;
}

.course-vjs-wrap--landscape .course-vjs-wrap__actions {
  position: fixed;
  z-index: 10051;
  left: 12px;
  bottom: calc(12px + env(safe-area-inset-bottom, 0px));
  margin: 0;
}

.course-vjs-wrap--landscape .course-vjs-wrap__btn {
  background: rgba(255, 255, 255, 0.92);
}
</style>
